import { createFileRoute, Link } from "@tanstack/react-router";
import { Award, ShieldCheck, MapPin, Wrench, Headphones, Sparkles } from "lucide-react";
import heroImg from "@/assets/brands-lifestyle.jpg";
import approachImg from "@/assets/smart-home.jpg";
import promiseImg from "@/assets/networking.jpg";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Apex AV — Denver Home Automation & AV Company" },
      { name: "description", content: "Apex AV is a licensed, insured Denver home automation & AV installer with decades of experience across the Denver Metro and Front Range." },
      { name: "keywords", content: "Denver AV company, local home automation installer Denver, licensed AV contractor Colorado, Denver smart home company, certified smart home dealer Denver, custom AV installer Denver, lighting control dealer Colorado, Front Range home theater company" },
      { property: "og:title", content: "About Apex AV — Denver Home Automation Experts" },
      { property: "og:description", content: "A Denver home automation and AV company built on industry-veteran craftsmanship." },
      { property: "og:url", content: "https://apexav.com/about" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "About Apex AV — Denver Home Automation Experts" },
      { name: "twitter:description", content: "A Denver home automation and AV company built on industry-veteran craftsmanship." },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/about" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "About Apex AV",
          url: "https://apexav.com/about",
          description: "Apex AV is a Denver home automation and AV installation company built on industry-veteran craftsmanship.",
          mainEntity: { "@id": "https://apexav.com/#organization" },
        }),
      },
    ],
  }),
  component: AboutPage,
});

const pillars = [
  {
    icon: Award,
    title: "Industry-veteran team",
    body: "Our installers and designers bring decades of combined experience from the top tier of custom AV — the same standards used in flagship integration firms, brought to Denver homes.",
  },
  {
    icon: ShieldCheck,
    title: "Licensed, insured, accountable",
    body: "Fully licensed and insured in Colorado, factory-certified on the platforms we install, and committed to the kind of clean, code-correct work you'd expect from a long-standing contractor.",
  },
  {
    icon: MapPin,
    title: "Built for the Front Range",
    body: "We design systems for Colorado homes — high-altitude HVAC integrations, mountain-facing patios, mixed-material construction, and the realities of life along the Front Range.",
  },
  {
    icon: Wrench,
    title: "Craftsmanship first",
    body: "Labeled wiring. Squared mounts. Tuned rooms. Documented programming. The work you see — and the work hidden in the wall — is held to the same standard.",
  },
  {
    icon: Headphones,
    title: "Real support, real people",
    body: "When you call, you reach the team that built your system. No tiered call centers, no ticket queues — just direct, knowledgeable support from the people who know your home.",
  },
  {
    icon: Sparkles,
    title: "Future-ready by design",
    body: "We engineer with headroom: extra conduit, scalable racks, open-standard control platforms. Your system grows with your home instead of being torn out and replaced.",
  },
];

function AboutPage() {
  return (
    <>
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Apex AV Denver smart home and home theater installation team serving Cherry Creek and Highlands Ranch" className="h-full w-full object-cover opacity-40" width={1920} height={1080} loading="eager" fetchPriority="high" decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background" />
        </div>
        <div className="relative container mx-auto px-6 pt-20 pb-12">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold text-brand-text uppercase tracking-wider">About Apex AV</p>
            <h1 className="mt-3 font-display text-5xl md:text-6xl font-bold">
              Denver's home for serious AV.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground">
              Apex AV is a Denver-based home automation and audio/video integration company built on the
              craftsmanship, certifications, and discipline of the custom AV industry's best. We design
              and install home theaters, whole-home audio, lighting control, motorized shading, and
              smart-home systems for homeowners across the Denver Metro and Colorado Front Range.
            </p>
            <p className="mt-4 text-muted-foreground">
              Our team has spent careers in this trade — programming whole-home control systems,
              commissioning architectural lighting, calibrating reference theaters, and pulling clean low-voltage
              in everything from Cherry Creek high-rises to Castle Rock new builds. Apex AV brings that
              depth of experience under one roof, with the responsiveness and ownership of a local company.
            </p>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 py-12">
        <h2 className="font-display text-3xl md:text-4xl font-bold">Why Denver homeowners choose Apex AV</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {pillars.map((p) => (
            <div key={p.title} className="rounded-xl border border-border bg-card p-6">
              <p.icon className="h-6 w-6 text-brand-text" />
              <h3 className="mt-4 font-display text-xl font-semibold">{p.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container mx-auto px-6 py-20">
        <div className="grid gap-12 md:grid-cols-2 max-w-5xl mx-auto">
          <div className="space-y-6">
            <div className="rounded-2xl overflow-hidden shadow-card aspect-[4/3]">
              <img src={approachImg} alt="Apex AV smart home design consultation at a Castle Pines Denver luxury home" loading="lazy" width={1280} height={960} className="h-full w-full object-cover" />
            </div>
            <div>
              <h2 className="font-display text-3xl font-bold">Our approach</h2>
              <p className="mt-4 text-muted-foreground">
                Every project starts with a conversation, not a catalog. We listen first, walk the space,
                and engineer a system around how you actually live — your routines, your rooms, your taste
                — instead of what's easiest to install. Our designs are documented, our scopes are clear,
                and there are no surprises on installation day.
              </p>
            </div>
          </div>
          <div className="space-y-6">
            <div className="rounded-2xl overflow-hidden shadow-card aspect-[4/3]">
              <img src={promiseImg} alt="Clean, labeled AV rack and structured wiring install by Apex AV in a Greenwood Village Denver home" loading="lazy" width={1280} height={960} className="h-full w-full object-cover" />
            </div>
            <div>
              <h2 className="font-display text-3xl font-bold">Our promise</h2>
              <p className="mt-4 text-muted-foreground">
                Clean wiring. Honest pricing. Reliable systems. We stand behind every install, and when
                technology evolves, we're here to evolve with it. Apex AV is built to be the integrator
                your home keeps — not the one you have to replace.
              </p>
            </div>
          </div>
        </div>
        <div className="mt-12 text-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-brand-foreground shadow-glow"
          >
            Start your project
          </Link>
        </div>
      </section>
    </>
  );
}
